import {
  PerformanceReportQuery,
  PerformanceReportRecord,
  PerformanceWorkerRequest,
  PerformanceWorkerResponse
} from './performanceReportTypes';

declare const BUILD_VERSION: string;
const WORKER_PATH = `/brand/_assets/js/performanceReportWorker.js?v=${BUILD_VERSION}`;
const REQUEST_TIMEOUT_MS = 5000;
const WORKER_IDLE_TIMEOUT_MS = 30000;

interface PendingRequest {
  resolve: (value: PerformanceReportRecord[] | { migrated: number } | void) => void;
  reject: (reason: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export class PerformanceReportClient {
  private static instance: PerformanceReportClient;
  private readonly pendingRequests = new Map<string, PendingRequest>();
  private worker: Worker | null = null;
  private idleTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public static getInstance(): PerformanceReportClient {
    if (!PerformanceReportClient.instance) {
      PerformanceReportClient.instance = new PerformanceReportClient();
    }

    return PerformanceReportClient.instance;
  }

  public async saveReport(report: PerformanceReportRecord): Promise<void> {
    await this.send('SAVE_REPORT', report);
  }

  public async getReports(query: PerformanceReportQuery): Promise<PerformanceReportRecord[]> {
    const response = await this.send('GET_REPORTS', query);
    return Array.isArray(response) ? response : [];
  }

  public async deleteExpired(now = Date.now()): Promise<void> {
    await this.send('DELETE_EXPIRED', { now });
  }

  public async migrateLegacy(reports: PerformanceReportRecord[]): Promise<{ migrated: number }> {
    const response = await this.send('MIGRATE_LEGACY', { reports });
    return typeof response === 'object' && response !== null && 'migrated' in response
      ? (response as { migrated: number })
      : { migrated: 0 };
  }

  public terminateWorker(): void {
    if (this.idleTimeoutId) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }

    const worker = this.worker;
    if (worker) {
      worker.terminate();
      this.worker = null;
    }

    this.rejectPendingRequests(new Error('Performance report worker terminated.'));
  }

  private send(
    type: PerformanceWorkerRequest['type'],
    payload: PerformanceWorkerRequest['payload']
  ): Promise<PerformanceReportRecord[] | { migrated: number } | void> {
    return new Promise((resolve, reject) => {
      const worker = this.getWorker();
      if (!worker) {
        reject(new Error('Performance report worker is unavailable.'));
        return;
      }

      const requestId = this.createRequestId();
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Performance worker request ${type} timed out.`));
      }, REQUEST_TIMEOUT_MS);

      this.pendingRequests.set(requestId, { resolve, reject, timeoutId });
      worker.postMessage({ requestId, type, payload } as PerformanceWorkerRequest);
      this.resetIdleTimeout();
    });
  }

  private getWorker(): Worker | null {
    if (this.worker || typeof Worker === 'undefined') {
      return this.worker;
    }

    try {
      this.worker = new Worker(WORKER_PATH);
      this.worker.onmessage = (event: MessageEvent<PerformanceWorkerResponse>) => {
        this.handleWorkerMessage(event.data);
      };
      this.worker.onerror = () => this.rejectPendingRequests(new Error('Performance report worker failed.'));
    } catch {
      this.worker = null;
    }

    return this.worker;
  }

  private handleWorkerMessage(response: PerformanceWorkerResponse): void {
    const { requestId, type, payload, error } = response;
    const pending = this.pendingRequests.get(requestId);
    if (!pending) {
      return;
    }

    clearTimeout(pending.timeoutId);
    this.pendingRequests.delete(requestId);

    if (type === 'SUCCESS') {
      pending.resolve(payload);
      return;
    }

    pending.reject(new Error(error ?? 'Performance report worker failed.'));
  }

  private resetIdleTimeout(): void {
    if (this.idleTimeoutId) {
      clearTimeout(this.idleTimeoutId);
    }

    this.idleTimeoutId = setTimeout(() => this.terminateWorker(), WORKER_IDLE_TIMEOUT_MS);
  }

  private requestIdCounter = 0;

  private createRequestId(): string {
    this.requestIdCounter++;

    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const values = new Uint32Array(2);
      crypto.getRandomValues(values);
      return `${Date.now()}-${values[0].toString(16)}${values[1].toString(16)}`;
    }

    return `${Date.now()}-${this.requestIdCounter.toString(16)}`;
  }

  private rejectPendingRequests(reason: Error): void {
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timeoutId);
      pending.reject(reason);
    }

    this.pendingRequests.clear();
    this.worker = null;
  }
}
